import React from 'react';
import { motion } from 'framer-motion';
import {
  Shield, Lock, Server, KeyRound, FileCheck, Users, Eye, Database,
  Globe, GitBranch, Mail, AlertTriangle, ArrowRight,
} from 'lucide-react';
import Button from '../components/common/Button';

const ease = [0.16, 1, 0.3, 1];

const SECTIONS = [
  {
    icon: Server,
    title: 'Data residency',
    items: [
      'Tenants hostable in UAE, KSA, Qatar or EU regions',
      'Customer data never crosses jurisdictional boundaries without explicit consent',
      'Per-region encryption keys, managed via dedicated KMS',
    ],
  },
  {
    icon: Lock,
    title: 'Encryption',
    items: [
      'AES-256-GCM at rest, TLS 1.3 in transit',
      'Field-level encryption for price, volume and counterparty data',
      'Customer-managed keys (CMK) available on enterprise plans',
    ],
  },
  {
    icon: KeyRound,
    title: 'Identity & access',
    items: [
      'SSO via SAML 2.0 and OIDC — Okta, Entra ID, Google',
      'SCIM 2.0 provisioning, with role mapping to your IdP groups',
      'Granular RBAC: trader, supervisor, compliance, read-only',
    ],
  },
  {
    icon: Eye,
    title: 'Audit & observability',
    items: [
      'Every field access, edit and approval is logged with user attribution',
      '7-year audit retention by default, exportable to your SIEM',
      'Real-time webhook stream for security events',
    ],
  },
  {
    icon: Database,
    title: 'Data isolation',
    items: [
      'Per-tenant database with no shared rows',
      'Models tuned on public market data — never on customer trades',
      'Verifiable data deletion on request, with cryptographic erasure',
    ],
  },
  {
    icon: GitBranch,
    title: 'Operational maturity',
    items: [
      'CI/CD with mandatory peer review on every change',
      'Automated dependency & container scanning (Trivy, Snyk)',
      'Third-party penetration testing on the security roadmap',
    ],
  },
];

const CERTIFICATIONS = [
  { name: 'SOC 2 Type I',         status: 'On roadmap · Q4 2026' },
  { name: 'ISO 27001',            status: 'On roadmap · 2027'    },
  { name: 'GDPR-aligned',         status: 'By design'            },
  { name: 'PDPL (KSA)-aligned',   status: 'By design'            },
  { name: 'PDPL (UAE)-aligned',   status: 'By design'            },
  { name: 'PDPPL (Qatar)-aligned', status: 'By design'           },
];

const SecurityPage = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-36 pb-24 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-radial-amber pointer-events-none" />
        <div className="absolute inset-0 grid-bg pointer-events-none" />

        <div className="container-prose relative">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber/10 ring-1 ring-amber/25 mb-7">
              <Shield size={13} className="text-amber" />
              <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light">Security</span>
            </div>
            <h1 className="text-hero font-bold text-canvas-white text-balance">
              Built to sit{' '}
              <span className="bg-gradient-to-r from-amber via-amber-light to-amber bg-clip-text text-transparent">
                inside the perimeter
              </span>{' '}
              your compliance team already trusts.
            </h1>
            <p className="mt-7 text-[17px] leading-relaxed text-ink-faint max-w-2xl">
              Trade confirmations contain some of the most sensitive data on a desk —
              counterparties, prices, volumes. We've built Core Trade so that a CISO
              review is fast, and a regulator review is faster.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posture */}
      <section className="py-20 bg-canvas-dark border-t border-white/[0.05]">
        <div className="container-prose">
          <div className="grid md:grid-cols-3 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.06]">
            {[
              { v: 'AES-256',  l: 'Encryption at rest',         icon: Lock },
              { v: 'TLS 1.3',  l: 'In transit',                 icon: Globe },
              { v: 'SOC 2',    l: 'Type I — on roadmap Q4 2026', icon: FileCheck },
            ].map(({ v, l, icon: Icon }) => (
              <div key={l} className="bg-canvas-black p-8 flex items-center justify-between">
                <div>
                  <p className="font-mono text-[28px] font-light text-canvas-white tracking-tight">{v}</p>
                  <p className="mt-1 text-[12.5px] text-ink-faint">{l}</p>
                </div>
                <Icon size={20} className="text-amber" strokeWidth={1.5} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed sections */}
      <section className="py-28 bg-canvas-black">
        <div className="container-prose">
          <div className="max-w-2xl mb-14">
            <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light mb-5">Controls</p>
            <h2 className="text-display font-bold text-canvas-white text-balance">
              The controls behind the platform.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {SECTIONS.map(({ icon: Icon, title, items }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.55, delay: (i % 2) * 0.08, ease }}
                className="p-8 rounded-2xl border border-white/[0.07] bg-canvas-surface/40"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-amber/10 ring-1 ring-amber/20 flex items-center justify-center">
                    <Icon size={17} className="text-amber" strokeWidth={1.6} />
                  </div>
                  <h3 className="text-[18px] font-semibold text-canvas-white tracking-tight">{title}</h3>
                </div>
                <ul className="space-y-3">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[13.5px] leading-relaxed text-ink-faint">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-amber flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-canvas-dark">
        <div className="container-prose">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light mb-5">Certifications</p>
              <h2 className="text-[28px] md:text-[32px] font-bold tracking-tight text-canvas-white text-balance">
                Where we are. And where we're going.
              </h2>
              <p className="mt-5 text-[14px] leading-relaxed text-ink-faint max-w-md">
                We won't pretend to certifications we don't have. Here's the honest
                state of our compliance posture.
              </p>
            </div>
            <div className="lg:col-span-8 divide-y divide-white/[0.06] border-y border-white/[0.06]">
              {CERTIFICATIONS.map((c) => (
                <div key={c.name} className="py-5 flex items-center justify-between">
                  <span className="text-[15.5px] font-medium text-canvas-white tracking-tight">{c.name}</span>
                  <span
                    className={`text-[12px] font-medium px-3 py-1 rounded-full ring-1 ${
                      c.status === 'By design'
                        ? 'bg-success/10 ring-success/25 text-success'
                        : 'bg-amber/10 ring-amber/25 text-amber-light'
                    }`}
                  >
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Responsible disclosure */}
      <section className="py-24 bg-canvas-black">
        <div className="container-prose grid md:grid-cols-2 gap-10">
          <div className="p-8 rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.025] to-transparent">
            <Users size={18} className="text-amber mb-4" strokeWidth={1.6} />
            <h3 className="text-[19px] font-semibold text-canvas-white tracking-tight mb-3">Customer-facing diligence</h3>
            <p className="text-[14px] leading-relaxed text-ink-faint mb-5">
              Detailed security questionnaires, SOC reports under NDA, architecture
              diagrams and penetration test summaries — available to enterprise prospects.
            </p>
            <Button href="mailto:hello@coretrade.ai" variant="outline" size="sm" withArrow>
              Request the security pack
            </Button>
          </div>
          <div className="p-8 rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.025] to-transparent">
            <AlertTriangle size={18} className="text-amber mb-4" strokeWidth={1.6} />
            <h3 className="text-[19px] font-semibold text-canvas-white tracking-tight mb-3">Found a vulnerability?</h3>
            <p className="text-[14px] leading-relaxed text-ink-faint mb-5">
              We take responsible disclosure seriously. If you've found something,
              email us and we'll respond within one business day.
            </p>
            <a
              href="mailto:hello@coretrade.ai"
              className="group inline-flex items-center gap-1.5 text-[14px] font-medium text-amber-light hover:text-amber transition-colors"
            >
              <Mail size={13} />
              hello@coretrade.ai
              <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default SecurityPage;
